import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_BACKOFF_MS = 30000;
const HEARTBEAT_MS = 30000;

function buildWsUrl(token) {
  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const url = new URL(base);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = '/api/chat-ws';
  url.search = `token=${encodeURIComponent(token)}`;
  return url.toString();
}

/**
 * Low-level WebSocket hook with reconnect + heartbeat.
 */
export default function useWebSocket({ enabled = true, onMessage } = {}) {
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const wsRef = useRef(null);
  const backoffRef = useRef(1000);
  const reconnectTimerRef = useRef(null);
  const heartbeatRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  const intentionalCloseRef = useRef(false);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!enabled || !token) {
      setConnectionStatus('disconnected');
      return;
    }

    intentionalCloseRef.current = false;
    clearTimers();

    try {
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (_) {
          /* ignore */
        }
      }

      setConnectionStatus((prev) => (prev === 'connected' ? prev : 'reconnecting'));
      const ws = new WebSocket(buildWsUrl(token));
      wsRef.current = ws;

      ws.onopen = () => {
        backoffRef.current = 1000;
        setConnectionStatus('connected');
        heartbeatRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, HEARTBEAT_MS);
      };

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload?.type === 'pong') return;
          onMessageRef.current?.(payload);
        } catch (_) {
          /* ignore malformed */
        }
      };

      ws.onerror = () => {
        setConnectionStatus('reconnecting');
      };

      ws.onclose = () => {
        clearTimers();
        setConnectionStatus('disconnected');
        if (!intentionalCloseRef.current && enabled) {
          const delay = backoffRef.current;
          backoffRef.current = Math.min(delay * 2, MAX_BACKOFF_MS);
          setConnectionStatus('reconnecting');
          reconnectTimerRef.current = setTimeout(connect, delay);
        }
      };
    } catch (_) {
      setConnectionStatus('disconnected');
    }
  }, [enabled, clearTimers]);

  useEffect(() => {
    connect();
    return () => {
      intentionalCloseRef.current = true;
      clearTimers();
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (_) {
          /* ignore */
        }
        wsRef.current = null;
      }
    };
  }, [connect, clearTimers]);

  const sendMessage = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify(payload));
    return true;
  }, []);

  return { connectionStatus, sendMessage, reconnect: connect };
}
